import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, Check, MessageCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { photoSrc } from "@/components/Reviews";
import { api, waLink } from "@/lib/api";
import { HeartButton } from "@/components/WishlistDock";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};
const item = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

function TourCard({ tour, index }) {
  const slug = tour.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30);
  return (
    <motion.article
      variants={item}
      whileHover={{ y: -6 }}
      className={`group bg-white rounded-2xl overflow-hidden border border-ink/10 shadow-sm hover:shadow-2xl hover:shadow-ink/10 transition-shadow duration-500 flex flex-col ${
        index % 4 === 0 ? "md:col-span-7" : index % 4 === 1 ? "md:col-span-5" : index % 4 === 2 ? "md:col-span-5" : "md:col-span-7"
      }`}
      data-testid={`tour-card-${slug}`}
    >
      <div className="relative h-56 sm:h-64 overflow-hidden">
        <HeartButton item={{ id: `tour-${tour.id}`, type: "tour", name: tour.title, slug: tour.slug, image: tour.image, meta: `${tour.duration} · ${tour.price}` }} className="absolute top-3 right-3 z-10" />
        <img
          src={photoSrc(tour.image)}
          alt={tour.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <span className="absolute top-4 left-4 bg-sand/95 text-ink text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full flex items-center gap-1.5">
          <Clock size={12} /> {tour.duration}
        </span>
        <span className="absolute bottom-4 right-4 bg-terra text-sand font-serif text-xl font-bold px-4 py-1.5 rounded-full shadow-lg" data-testid={`tour-price-${slug}`}>
          {tour.price}
        </span>
      </div>
      <div className="p-6 sm:p-7 flex flex-col flex-1">
        <h3 className="font-serif text-xl sm:text-2xl font-bold text-ink leading-snug">
          <Link to={`/tours/${tour.slug}`} className="hover:text-terra transition-colors" data-testid={`tour-title-link-${slug}`}>{tour.title}</Link>
        </h3>
        <p className="mt-2 text-sm text-ink/70 leading-relaxed">{tour.description}</p>
        <ul className="mt-4 grid grid-cols-2 gap-2">
          {tour.features?.map((f) => (
            <li key={f} className="flex items-center gap-1.5 text-xs text-ink/75">
              <Check size={13} className="text-terra shrink-0" /> {f}
            </li>
          ))}
        </ul>
        <div className="mt-6 pt-4 border-t border-ink/10 flex items-center gap-3 mt-auto">
          <Link
            to={`/tours/${tour.slug}`}
            data-testid={`tour-details-${slug}`}
            className="flex items-center gap-1.5 text-sm font-semibold text-terra hover:gap-3 transition-all duration-200"
          >
            Full itinerary <ArrowRight size={15} />
          </Link>
          <a
            href={waLink(`Hi Random Rider! I want to book the "${tour.title}" tour (${tour.price}).`)}
            target="_blank"
            rel="noreferrer"
            data-testid={`tour-whatsapp-${slug}`}
            className="ml-auto flex items-center gap-1.5 bg-[#25D366]/10 text-[#1da851] text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#25D366] hover:text-white transition-colors duration-200"
          >
            <MessageCircle size={14} /> Book
          </a>
        </div>
      </div>
    </motion.article>
  );
}

function Section({ id, eyebrow, title, blurb, tours, testId }) {
  return (
    <section id={id} className="py-20 lg:py-28" data-testid={testId}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra mb-3">{eyebrow}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink">{title}</h2>
          <p className="mt-4 text-ink/70 leading-relaxed">{blurb}</p>
        </motion.div>
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8"
        >
          {tours.map((t, i) => (
            <TourCard key={t.id} tour={t} index={i} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default function TourSections() {
  const [tours, setTours] = useState([]);
  useEffect(() => {
    api.get("/tours").then((r) => setTours(r.data)).catch(() => {});
  }, []);
  const local = tours.filter((t) => t.category === "local");
  const odisha = tours.filter((t) => t.category === "odisha");

  return (
    <>
      <Section
        id="puri-tours"
        testId="puri-tours-section"
        eyebrow="01 · Puri Local Sightseeing"
        title="Puri, the way locals live it"
        blurb="Temple darshan at dawn, Blue Flag beaches at dusk, craft villages in between — half-day and full-day circuits with a local at the wheel."
        tours={local}
      />
      <div className="bg-surface">
        <Section
          id="odisha-tours"
          testId="odisha-tours-section"
          eyebrow="02 · All Odisha Packages"
          title="One state. A thousand stories."
          blurb="From the Konark Sun Temple to Chilika's Irrawaddy dolphins — curated multi-day circuits across Odisha with stays, drivers and zero hidden costs."
          tours={odisha}
        />
      </div>
    </>
  );
}
